
import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import { getCelebrities } from "../../../../services/celebrityService";

const SearchList = ({ searchQuery }) => {
  const [celebrities, setCelebrities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCelebs, setTotalCelebs] = useState(0);
  const { isAuthenticated, user } = useContext(AuthContext);

  useEffect(() => {
    // Reset to page 1 when search query changes
    setCurrentPage(1);
    loadCelebrities(1, searchQuery);
  }, [searchQuery]);

  const loadCelebrities = async (page = 1, search = "") => {
    try {
      setLoading(true);
      const response = await getCelebrities(page, 12, search); // Changed from 100 to 12 for better pagination

      if (response.message === "Celebrities retrieved successfully") {
        // Filter out the current user if they are a celebrity
        const filteredData = response.data.filter(celeb => {
          // If user is not authenticated or not a celeb, show all
          if (!isAuthenticated || user?.userType !== 'celeb') return true;

          // If user is a celeb, hide their own profile
          return celeb._id !== user._id;
        });

        const transformedCelebs = filteredData.map((celeb) => {
          // Ensure image URL is properly formatted
          let imageUrl = celeb.picture;
          if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
            imageUrl = 'assets/img/doctor-grid/celeb.jpg';
          }

          const hasShoutoutPricing = celeb.shoutout && celeb.shoutout.length > 0;
          const shoutoutPrice = hasShoutoutPricing ? parseFloat(celeb.shoutout[0].price) : null;

          return {
            id: celeb._id,
            name: celeb.displayName,
            category: celeb.celebType || 'Entertainer',
            image: imageUrl,
            price: shoutoutPrice,
            hasPricing: hasShoutoutPricing,
            // Store original data for shoutout pricing
            originalData: celeb
          };
        });

        setCelebrities(transformedCelebs);
        setTotalPages(response.pagination?.total || 1);
        setTotalCelebs(transformedCelebs.length);
        setCurrentPage(response.pagination?.current || 1);
      } else {
        setError(response.message || 'Failed to load celebrities');
      }
    } catch (err) {
      console.error('Error loading celebrities:', err);
      const errorMessage =
        err.message ||
        err.response?.data?.message ||
        'Failed to load celebrities';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load celebrities when page changes
  useEffect(() => {
    if (currentPage > 1) {
      loadCelebrities(currentPage, searchQuery);
    }
  }, [currentPage]);

  if (loading && celebrities.length === 0) {
    return (
      <div className="row justify-content-center">
        <div className="col-md-6 text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Searching celebrities{searchQuery ? ` for "${searchQuery}"` : ''}...</p>
        </div>
      </div>
    );
  }

  if (error && celebrities.length === 0) {
    return (
      <div className="row justify-content-center">
        <div className="col-md-6 text-center py-5">
          <div className="alert alert-danger">
            <h4>Error Loading Celebrities</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => loadCelebrities(1, searchQuery)}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Results Count */}
      <div className="row align-items-center mb-4">
        <div className="col-md-6">
          <div className="mb-4">
            <h3>
              Showing <span className="text-secondary">{celebrities.length}</span> Celebrities
              {searchQuery && <span> for "{searchQuery}"</span>}
            </h3>
            <p className="text-muted">Page {currentPage} of {totalPages} ({totalCelebs} total)</p>
          </div>
        </div>
      </div>

      {/* Celebrities Grid */}
      <div className="row">
        {celebrities.length > 0 ? (
          celebrities.map((celebrity, index) => (
            <div className="col-xxl-4 col-md-6" key={`${celebrity.id}-${index}`}>
              <div className="card">
                {/* Make image area clickable */}
                {celebrity.hasPricing ? (
                  <Link
                    to={isAuthenticated ? `/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&shoutoutPricing=${encodeURIComponent(JSON.stringify(celebrity.originalData.shoutout || []))}` : "#"}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    className="card-image-link"
                  >
                    <div className="card-img card-img-hover">
                      <img
                        src={celebrity.image}
                        alt={celebrity.name}
                        style={{
                          width: '100%',
                          height: '240px',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          cursor: 'pointer'
                        }}
                      />
                      <div className="grid-overlay-item d-flex align-items-center justify-content-between">
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="card-img card-img-hover">
                    <img
                      src={celebrity.image}
                      alt={celebrity.name}
                      style={{
                        width: '100%',
                        height: '240px',
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                    <div className="grid-overlay-item d-flex align-items-center justify-content-between">
                    </div>
                  </div>
                )}

                <div className="card-body p-0">
                  <div className="d-flex active-bar align-items-center justify-content-between p-3">
                    {celebrity.category}
                  </div>
                  <div className="p-3 pt-0">
                    <div className="doctor-info-detail mb-3 pb-3">
                      <h3 className="mb-1">
                        {celebrity.name}
                      </h3>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="mb-1">Shout out fee</p>
                        <h3 className="text-orange">
                          {celebrity.hasPricing ? `$${celebrity.price}` : 'Not Set'}
                        </h3>
                      </div>
                      {isAuthenticated ? (
                        celebrity.hasPricing ? (
                          // Show booking link only when pricing is available
                          <Link
                            to={`/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&shoutoutPricing=${encodeURIComponent(JSON.stringify(celebrity.originalData.shoutout || []))}`}
                            className="btn btn-md btn-dark d-inline-flex align-items-center rounded-pill"
                          >
                            <i className="isax isax-card-pos5 me-1" />
                            Request
                          </Link>
                        ) : (
                          // Show disabled button when no pricing
                          <button
                            className="btn btn-md btn-secondary d-inline-flex align-items-center rounded-pill"
                            disabled
                            title="This celebrity hasn't set up their pricing yet"
                          >
                            <i className="isax isax-card-pos5 me-1" />
                            No Pricing
                          </button>
                        )
                      ) : (
                        <Link
                          to="/login"
                          className="btn btn-md btn-outline-dark d-inline-flex align-items-center rounded-pill"
                        >
                          <i className="isax isax-login me-1" />
                          Login to Request
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-12 text-center py-5">
            <h4>No celebrities found</h4>
            <p>{searchQuery ? `No results found for "${searchQuery}"` : 'No celebrities available'}</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="pagination-container d-flex justify-content-center">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                    >
                      Previous
                    </button>
                  </li>

                  {/* Generate page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                      >
                        {pageNum}
                      </button>
                    </li>
                  ))}

                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator for pagination */}
      {loading && celebrities.length > 0 && (
        <div className="row mt-3">
          <div className="col-12 text-center">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading page {currentPage}...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchList;

// import React, { useState, useEffect, useContext } from "react";
// import { Link } from "react-router-dom";
// import { AuthContext } from "../../../../context/AuthContext";
// import { getCelebrities } from "../../../../services/celebrityService";

// const SearchList = ({ searchQuery }) => {
//   const [celebrities, setCelebrities] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [totalCelebs, setTotalCelebs] = useState(0);
//   const { isAuthenticated } = useContext(AuthContext);


//   useEffect(() => {
//     // Reset to page 1 when search query changes
//     setCurrentPage(1);
//     loadCelebrities(1, searchQuery);
//   }, [searchQuery]);

//   const loadCelebrities = async (page = 1, search = "") => {
//     try {
//       setLoading(true);
//       const response = await getCelebrities(page, 100, search);

//       if (response.message === "Celebrities retrieved successfully") {
//         const transformedCelebs = response.data.map((celeb) => {

//           const hasShoutoutPricing = celeb.shoutout && celeb.shoutout.length > 0;
//           const shoutoutPrice = hasShoutoutPricing ? parseFloat(celeb.shoutout[0].price) : null;

//           return {
//             id: celeb._id,
//             name: celeb.displayName,
//             category: celeb.celebType || 'Entertainer',
//             image: celeb.picture,
//             price: shoutoutPrice,
//             hasPricing: hasShoutoutPricing,
//           };
//         });

//         setCelebrities(transformedCelebs);
//         setTotalPages(response.pagination?.total || 1);
//         setTotalCelebs(response.pagination?.totalCelebs || 0);
//         setCurrentPage(response.pagination?.current || 1);
//       } else {
//         setError(response.message || 'Failed to load celebrities');
//       }
//     } catch (err) {
//       console.error('Error loading celebrities:', err);
//       setError(err.message || 'Failed to load celebrities');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const loadMore = () => {
//     if (currentPage < totalPages) {
//       const nextPage = currentPage + 1;
//       setCurrentPage(nextPage);
//       loadCelebrities(nextPage, searchQuery);
//     }
//   };

//   if (loading && celebrities.length === 0) {
//     return (
//       <div className="row justify-content-center">
//         <div className="col-md-6 text-center py-5">
//           <div className="spinner-border text-primary" role="status">
//             <span className="visually-hidden">Loading...</span>
//           </div>
//           <p className="mt-2">Searching celebrities{searchQuery ? ` for "${searchQuery}"` : ''}...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error && celebrities.length === 0) {
//     return (
//       <div className="row justify-content-center">
//         <div className="col-md-6 text-center py-5">
//           <div className="alert alert-danger">
//             <h4>Error Loading Celebrities</h4>
//             <p>{error}</p>
//             <button className="btn btn-primary" onClick={() => loadCelebrities(1, searchQuery)}>
//               Try Again
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="row">
//       {/* Results Count */}
//       <div className="col-md-12 mb-4">
//         <h4>
//           Showing <span className="text-secondary">{celebrities.length}</span> Celebrities
//           {searchQuery && <span> for "{searchQuery}"</span>}
//         </h4>
//         <p className="text-muted">Page {currentPage} of {totalPages} ({totalCelebs} total)</p>
//       </div>

//       {/* Celebrities Grid */}
//       {celebrities.length > 0 ? (
//         celebrities.map((celebrity, index) => (

//           <div className="col-xxl-3 col-lg-4 col-md-6" key={`${celebrity.id}-${index}`}>
//             <div className="card">
//               {/* Make image area clickable */}
//               {isAuthenticated && celebrity.hasPricing ? (
//                 <Link
//                   to={`/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&rating=${celebrity.rating}`}
//                   style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
//                   className="card-image-link"
//                 >
//                   <div className="card-img card-img-hover">
//                     <img
//                       src={celebrity.image}
//                       alt={celebrity.name}
//                       style={{
//                         width: '100%',
//                         height: '240px',
//                         objectFit: 'cover',
//                         objectPosition: 'center',
//                         cursor: 'pointer'
//                       }}
//                     />
//                     <div className="grid-overlay-item d-flex align-items-center justify-content-between">
//                     </div>
//                   </div>
//                 </Link>
//               ) : (
//                 <div className="card-img card-img-hover">
//                   <img
//                     src={celebrity.image}
//                     alt={celebrity.name}
//                     style={{
//                       width: '100%',
//                       height: '240px',
//                       objectFit: 'cover',
//                       objectPosition: 'center'
//                     }}
//                   />
//                   <div className="grid-overlay-item d-flex align-items-center justify-content-between">
//                   </div>
//                 </div>
//               )}

//               <div className="card-body p-0">
//                 <div className="d-flex active-bar align-items-center justify-content-between p-3">
//                   {celebrity.category}
//                 </div>
//                 <div className="p-3 pt-0">
//                   <div className="doctor-info-detail mb-3 pb-3">
//                     <h3 className="mb-1">
//                       {celebrity.name}
//                     </h3>
//                   </div>
//                   <div className="d-flex align-items-center justify-content-between">
//                     <div>
//                       <p className="mb-1">Shout out fee</p>
//                       <h3 className="text-orange">
//                         {celebrity.hasPricing ? `$${celebrity.price}` : 'Not Set'}
//                       </h3>
//                     </div>
//                     {isAuthenticated ? (
//                       celebrity.hasPricing ? (
//                         // Show booking link only when pricing is available
//                         <Link
//                           to={`/booking?celebId=${celebrity.id}&name=${encodeURIComponent(celebrity.name)}&category=${encodeURIComponent(celebrity.category)}&image=${encodeURIComponent(celebrity.image)}&price=${celebrity.price}&rating=${celebrity.rating}`}
//                           className="btn btn-md btn-dark d-inline-flex align-items-center rounded-pill"
//                         >
//                           <i className="isax isax-card-pos5 me-1" />
//                           Request
//                         </Link>
//                       ) : (
//                         // Show disabled button when no pricing
//                         <button
//                           className="btn btn-md btn-secondary d-inline-flex align-items-center rounded-pill"
//                           disabled
//                           title="This celebrity hasn't set up their pricing yet"
//                         >
//                           <i className="isax isax-card-pos5 me-1" />
//                           No Pricing
//                         </button>
//                       )
//                     ) : (
//                       <Link
//                         to="/login"
//                         className="btn btn-md btn-outline-dark d-inline-flex align-items-center rounded-pill"
//                       >
//                         <i className="isax isax-login me-1" />
//                         Login to Request
//                       </Link>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         ))
//       ) : (
//         <div className="col-12 text-center py-5">
//           <h4>No celebrities found</h4>
//           <p>{searchQuery ? `No results found for "${searchQuery}"` : 'No celebrities available'}</p>
//         </div>
//       )}

//       {/* Load More Button */}
//       {currentPage < totalPages && (
//         <div className="col-md-12">
//           <div className="text-center mb-4">
//             <button
//               onClick={loadMore}
//               className="btn btn-md btn-primary-gradient d-inline-flex align-items-center rounded-pill"
//               disabled={loading}
//             >
//               {loading ? (
//                 <>
//                   <div className="spinner-border spinner-border-sm me-2" role="status">
//                     <span className="visually-hidden">Loading...</span>
//                   </div>
//                   Loading...
//                 </>
//               ) : (
//                 <>
//                   <i className="isax isax-d-cube-scan5 me-2" />
//                   Load More
//                 </>
//               )}
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default SearchList;